// eslint-disable-next-line no-restricted-imports
import moment, { type Moment } from "moment";

import type { CustomTimeRange, Metric, TableData, TemporalResolution } from "../utils/types";
import { TemporalResolutions } from "../utils/types";
import { formatValue } from "../strings/type-formatting";
import type { Align } from "../utils/metrics";
import { columnsForMetrics, getSort, includeMetricDecimal } from "../utils/metrics";
import { marketingColumnTooltips } from "../strings/tooltips";
import { xDomainSteps as xDomainSteps2, xFormatDate as xFormatDate2 } from "../utils/custom-time-range-helpers";
import { Dimensions, MetricTypes } from "../autogen/metrics";
import type { DashboardOutput } from "../utils/fetch-api";
import { toTableRows } from "../utils/dashboard-output-helpers";

export enum AdPlatform {
  META_ADS = "META_ADS",
  GOOGLE_ADS = "GOOGLE_ADS",
}

const DATA_LABELS: Partial<Record<MetricTypes, string>> = {
  [MetricTypes.AD_SPEND]: "spend",
  [MetricTypes.AD_COST_PER_CONVERSION]: "cc",
  [MetricTypes.AD_ATTRIBUTED_CONVERSION_COUNT]: "conversions",
  [MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS]: "roas",
};

const getMetricAlias = (metric: MetricTypes, platform: AdPlatform) => {
  const platformPrefix = {
    [AdPlatform.META_ADS]: "META",
    [AdPlatform.GOOGLE_ADS]: "GOOGLE",
  }[platform];
  return `${platformPrefix}_${metric}`;
};

const getFormatters3 = (outputMetrics, platform: AdPlatform) => {
  const _formatter = (metricType) => {
    return (value: string) => {
      return formatValue({
        value,
        metricType: outputMetrics.find((metric) => metric.name === metricType),
        useDash: false,
        rateToFixed: 2,
        withDecimal: includeMetricDecimal(metricType),
      });
    };
  };
  return {
    formatSpend: _formatter(getMetricAlias(MetricTypes.AD_SPEND, platform)),
    formatCC: _formatter(getMetricAlias(MetricTypes.AD_COST_PER_CONVERSION, platform)),
    formatRoas: _formatter(getMetricAlias(MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS, platform)),
  };
};

export const getGoogleInsightsSummaryCards2 = (data, timeRange: CustomTimeRange) => {
  return getInsightsSummaryCards2(
    data,
    timeRange,
    [
      MetricTypes.AD_SPEND,
      MetricTypes.AD_ATTRIBUTED_CONVERSION_COUNT,
      MetricTypes.AD_COST_PER_CONVERSION,
      MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS,
    ],
    AdPlatform.GOOGLE_ADS
  );
};

const getRelevantDaySet = (day, resolution: TemporalResolution, dataByDay) => {
  const thisYear = dataByDay?.[day.valueOf()];
  const nextThisYear = dataByDay?.[day.clone().add(1, resolution.duration).valueOf()];
  let sameDayLastYear = day.clone().subtract(1, "year").valueOf();
  if (resolution.resolution === TemporalResolutions.WEEKLY.resolution) {
    sameDayLastYear = day.clone().subtract(1, "year").startOf("week").valueOf();
  }
  const lastYear = dataByDay?.[sameDayLastYear];
  return {
    thisYear,
    nextThisYear,
    lastYear,
  };
};

export const getInsightsOverviewChartData2 = (
  data: DashboardOutput,
  timeRange: CustomTimeRange,
  temporalResolution: TemporalResolution,
  platform: AdPlatform
) => {
  const steps = xDomainSteps2(timeRange.start, timeRange.end, temporalResolution);

  const dataByDay = {};
  data.data.map((row) => {
    dataByDay[moment(row["day"]).valueOf()] = row;
  });

  const chartData = steps.map((day: Moment) => {
    const { thisYear, nextThisYear, lastYear } = getRelevantDaySet(day, temporalResolution, dataByDay);

    const extract = (metric) => {
      const platformisedMetric = getMetricAlias(metric, platform);
      const currentValue = thisYear?.[platformisedMetric];
      const referenceValue = lastYear?.[platformisedMetric];
      const nextValue = nextThisYear?.[platformisedMetric];
      const continuation =
        (!currentValue || (currentValue && !nextValue)) && referenceValue ? parseFloat(referenceValue) : null;

      return {
        [`${DATA_LABELS[metric]}Current`]: currentValue,
        [`${DATA_LABELS[metric]}Reference`]: referenceValue,
        [`${DATA_LABELS[metric]}RefCont`]: continuation,
      };
    };

    return {
      day: xFormatDate2(day, temporalResolution, steps[0], steps[steps.length - 1]),
      ...extract(MetricTypes.AD_SPEND),
      ...extract(MetricTypes.AD_COST_PER_CONVERSION),
      ...extract(MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS),
    };
  });

  return { chartData, ...getFormatters3(data.metrics, platform) };
};

export const getSpendCpConversionRoasChartData2 = (
  data: DashboardOutput,
  timeRange: CustomTimeRange,
  temporalResolution: TemporalResolution,
  platform: AdPlatform
) => {
  const steps = xDomainSteps2(timeRange.start, timeRange.end, temporalResolution);
  const campaignTypeIndex = 0; // Ew.
  const types = [...new Set(data.groups.map((group) => group[campaignTypeIndex]))];

  const dataByDay = {};
  data.data.map((row) => {
    dataByDay[moment(row["day"]).valueOf()] = row;
  });

  const chartData = steps.map((day: Moment) => {
    const dayData = { day: xFormatDate2(day, temporalResolution, steps[0], steps[steps.length - 1]) };
    const { thisYear, nextThisYear, lastYear } = getRelevantDaySet(day, temporalResolution, dataByDay);
    types.map((type) => {
      const extract = (metric) => {
        const platformisedMetric = getMetricAlias(metric, platform);
        const current = thisYear?.[type + platformisedMetric];
        const reference = lastYear?.[type + platformisedMetric];
        const next = nextThisYear?.[type + platformisedMetric];
        const continuation = (!current || (current && !next)) && reference ? parseFloat(reference) : null;

        dayData[`${type}${DATA_LABELS[metric]}Current`] = current;
        dayData[`${type}${DATA_LABELS[metric]}Reference`] = reference;
        dayData[`${type}${DATA_LABELS[metric]}RefCont`] = continuation;
      };
      extract(MetricTypes.AD_SPEND);
      extract(MetricTypes.AD_COST_PER_CONVERSION);
      extract(MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS);
    });
    return dayData;
  });

  return { types, chartData, ...getFormatters3(data.metrics, platform) };
};

const getColumnsAndMetrics = (dimensions, metrics) => {
  const metricCols = columnsForMetrics(metrics);
  const groupByDimensionCols = dimensions.map((dim) => {
    return {
      name: dim,
      id: dim,
      isLink: false,
      align: "left" as Align,
    };
  });
  return {
    columns: [...groupByDimensionCols, ...metricCols],
    metrics,
  };
};

export const getCampaignPerformanceTableData2 = (
  data: DashboardOutput,
  metricTypes: MetricTypes[],
  platform: AdPlatform
) => {
  // TODO: Consider getting this from the dashboard output
  const campaignNameIndex = 1;
  const campaignTypeIndex = 2;
  const types = [...new Set(data.groups.map((group) => group[campaignTypeIndex]))];

  const { columns } = getColumnsAndMetrics(
    [Dimensions.CAMPAIGN],
    data.metrics.filter((m) => metricTypes.includes(m.name as MetricTypes))
  );

  return types.map((type) => {
    const groupsOfType = data.groups.filter((group) => group[campaignTypeIndex] === type);

    return {
      campaignType: type,
      tableData: {
        size: "small",
        initialPageSize: 5,
        columns,
        data: groupsOfType.map((group) => {
          const keys = Object.keys(data.summary).filter((v) => v.includes(group.toString()));
          const row = {};
          keys.map((key) => {
            const metricOnly = key.slice(group.toString().length);
            const metric = data.metrics.find((m) => m.name === metricOnly);
            row[getSort(metricOnly as MetricTypes)] = data.summary[key];
            row[metricOnly] = formatValue({ value: data.summary[key], metricType: metric });
          });
          row[Dimensions.CAMPAIGN] = group[campaignNameIndex];
          row[getSort(Dimensions.CAMPAIGN)] = group[campaignNameIndex];
          return row;
        }),
        getToolTipForColumn: marketingColumnTooltips,
        sortBy: [{ id: getMetricAlias(MetricTypes.AD_SPEND, platform), desc: true }],
      },
    };
  });
};

export const getMetaInsightsSummaryCards2 = (data: TableData, timeRange: CustomTimeRange) => {
  return getInsightsSummaryCards2(
    data,
    timeRange,
    [MetricTypes.AD_SPEND, MetricTypes.AD_COST_PER_CONVERSION, MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS],
    AdPlatform.META_ADS
  );
};

const getInsightsSummaryCards2 = (data, timeRange: CustomTimeRange, metrics: MetricTypes[], platform: AdPlatform) => {
  const rv = {};
  metrics.map((metric) => {
    const platformisedMetric = getMetricAlias(metric, platform);
    rv[DATA_LABELS[metric]] = formatValue({
      value: data.summary?.[platformisedMetric],
      metricType: data.metrics.find((m) => m.name === platformisedMetric),
      withDecimal: includeMetricDecimal(metric),
    });
  });
  return rv;
};

export const getSummaryTableData2 = (
  data: DashboardOutput,
  metricTypes: MetricTypes[],
  dimension: Dimensions,
  platform: AdPlatform
) => {
  const outputMetrics = [...data.metrics.filter((m) => metricTypes.includes(m.name as MetricTypes))];

  const adSpendMetric: Metric = {
    name: MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND,
    type: "PERCENTAGE",
    success_direction: "down",
  };
  outputMetrics.splice(1, 0, adSpendMetric);

  const totalSpend = Object.keys(data.data).reduce((val, key) => {
    return val + data.data[key][getMetricAlias(MetricTypes.AD_SPEND, platform)];
  }, 0);

  const { columns } = getColumnsAndMetrics([dimension], outputMetrics);
  const { data: rows } = toTableRows(data, dimension);
  rows.map((row) => {
    const spendVal = +row[getSort(getMetricAlias(MetricTypes.AD_SPEND, platform))];
    const percentVal = totalSpend === 0 ? 0 : (spendVal * 100) / totalSpend;
    row[getSort(MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND)] = percentVal;
    row[MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND] = formatValue({
      value: percentVal.toString(),
      metricType: adSpendMetric,
    });
  });

  return { columns, data: rows, sortBy: [{ id: getMetricAlias(MetricTypes.AD_SPEND, platform), desc: true }] };
};
